'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Collapsible } from '../../../../components/collapsible';
import { MetaDefault } from '../../../../components/typography';
import { type TracePhase, type TraceToolCall } from '../types';
import { useEntityResolver } from '../context/EntityResolverContext';
import { useTraceEntityNavigation } from '../context/TraceEntityNavigationContext';
import { renderToolOutput } from '../utils/tool-output-renderer';
import {
  getTraceToolRenderConfig,
  isBrowserToolName,
  normalizeToolName,
} from '../utils/tool-config';
import { formatTraceDurationLabel } from '../utils/trace-duration';
import { isSkillFilePath } from '../utils/read-file-tool';
import {
  isCoPilotLabelOnlyLookupTool,
  shouldShowCoPilotInlineToolArgsPreview,
} from '../utils/co-pilot-trace-display';
import { shouldShowTraceToolShimmer } from '../utils/trace-text-shimmer';
import { getInlineToolSummary } from '../utils/inline-tool-summary';
import {
  getInlineToolDetailRows,
  getInlineToolNavigationTarget,
} from '../utils/inline-tool-details';
import { resolveTraceToolErrorDisplay } from '../utils/resolve-trace-tool-error';
import { getInlineToolEntityList } from '../utils/inline-tool-entity-list';
import { TRACE_INLINE_SCROLL_MAX_HEIGHT } from '../utils/trace-inline-layout';
import { TraceInlineStepRow } from './TraceInlineStepRow';
import { TraceInlineArgsList } from './TraceInlineArgsList';
import { TraceInlineErrorBlock } from './TraceInlineErrorBlock';
import { TraceInlineEntityList } from './TraceInlineEntityList';
import { TraceInlineOpenEntityAction } from './TraceInlineOpenEntityAction';

type InlineToolCardProps = {
  toolCall: TraceToolCall;
  displayName: string;
  tracePhase?: TracePhase;
  onHideMessage?: () => void;
};

const READ_FILE_PATH_KEYS = [
  'filePath',
  'file_path',
  'target_file',
  'path',
] as const;

function getPathFromData(data: unknown): string {
  if (!data || typeof data !== 'object') {
    return '';
  }

  const dataRecord = data as Record<string, unknown>;
  const match = READ_FILE_PATH_KEYS.map((key) => dataRecord[key]).find(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  );

  return match ?? '';
}

export function InlineToolCard({
  toolCall,
  displayName,
  tracePhase = 'attack',
  onHideMessage,
}: InlineToolCardProps) {
  const t = useTranslations('designSystem.chat');
  const { resolve } = useEntityResolver();
  const onOpenEntity = useTraceEntityNavigation();
  const normalizedName = normalizeToolName(toolCall.name);
  const renderConfig = getTraceToolRenderConfig(toolCall.name);

  const readFilePath =
    getPathFromData(toolCall.args) || getPathFromData(toolCall.output);
  const isSkillRead =
    normalizedName === 'read-file' && isSkillFilePath(readFilePath);
  const isCoPilotLabelOnly = isCoPilotLabelOnlyLookupTool({
    toolName: toolCall.name,
    tracePhase,
    filePath: readFilePath,
  });
  const isLabelOnly = isSkillRead || isCoPilotLabelOnly;
  const resolvedDisplayName =
    tracePhase === 'co-pilot' && isSkillRead ? 'Read skill' : displayName;

  const showCoPilotLookupArgs = shouldShowCoPilotInlineToolArgsPreview({
    toolName: toolCall.name,
    tracePhase,
  });
  const args = toolCall.args ?? {};
  const hasArgKeys = Object.keys(args).length > 0;
  const detailRows =
    hasArgKeys &&
    !renderConfig.hideArgs &&
    !isCoPilotLabelOnly &&
    showCoPilotLookupArgs
      ? getInlineToolDetailRows({ toolName: toolCall.name, args, resolve })
      : [];
  const hasArgs = detailRows.length > 0;
  const hasOutput = Boolean(toolCall.output);
  const hasError = Boolean(toolCall.error);
  const isErrored = hasError || toolCall.status === 'error';
  const shouldShowRawError = hasError && !renderConfig.hideRawError;
  const toolOutputForRender =
    toolCall.output || (renderConfig.hideRawError ? toolCall.error : undefined);
  const hasResult = (hasOutput || hasError) && !isLabelOnly;
  const canExpand = (hasArgs || hasResult) && !isLabelOnly;

  const [isExpanded, setIsExpanded] = useState(
    tracePhase === 'co-pilot' && canExpand && hasResult,
  );
  const isBrowserSessionTool = isBrowserToolName(toolCall.name);
  const renderedToolOutput =
    isExpanded && toolOutputForRender
      ? renderToolOutput({
          toolName: toolCall.name,
          output: toolOutputForRender,
          args: toolCall.args,
          error: toolCall.error,
          toolCallId: toolCall.id,
        })
      : null;
  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });

  const errorDisplay = isErrored
    ? resolveTraceToolErrorDisplay({
        error: toolCall.error,
        metadata: toolCall.metadata,
      })
    : undefined;
  const errorSummary =
    errorDisplay && !isLabelOnly
      ? renderConfig.hideRawError
        ? errorDisplay.metadataSummary
        : errorDisplay.summary
      : undefined;
  const summary =
    !isLabelOnly && showCoPilotLookupArgs && hasArgKeys
      ? getInlineToolSummary({ toolName: toolCall.name, args, resolve })
      : undefined;
  const navigationTarget =
    onOpenEntity && !isLabelOnly
      ? getInlineToolNavigationTarget({
          toolName: toolCall.name,
          args,
          resolve,
        })
      : undefined;
  const entityListItems = getInlineToolEntityList({
    toolName: toolCall.name,
    output: toolCall.output,
  });

  const openAction =
    navigationTarget && onOpenEntity ? (
      <TraceInlineOpenEntityAction
        target={navigationTarget}
        onOpenEntity={onOpenEntity}
      />
    ) : null;

  const showStatusMeta =
    isBrowserSessionTool || normalizedName === 'load-tools';
  const durationLabel = showStatusMeta
    ? formatTraceDurationLabel(toolCall.timing?.durationMs)
    : undefined;

  const row = (
    <TraceInlineStepRow
      label={resolvedDisplayName}
      summary={summary}
      errorSummary={errorSummary}
      durationLabel={durationLabel}
      isError={isErrored}
      canExpand={canExpand}
      isExpanded={isExpanded}
      shimmer={showShimmer}
      onHideMessage={onHideMessage}
    />
  );

  if (!canExpand) {
    return (
      <HStack className="group" w="full" gap={1} alignItems="center">
        {row}
        {openAction}
      </HStack>
    );
  }

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
      lazyMount={isBrowserSessionTool}
    >
      <HStack className="group" w="full" gap={1} alignItems="center">
        <Collapsible.Trigger asChild>{row}</Collapsible.Trigger>
        {openAction}
      </HStack>

      <Collapsible.Content>
        <Box mt={1} mb={2} py={1}>
          <VStack alignItems="stretch" gap={3}>
            {shouldShowRawError && errorDisplay ? (
              <TraceInlineErrorBlock display={errorDisplay} />
            ) : null}

            <TraceInlineArgsList rows={detailRows} />

            {entityListItems ? (
              <TraceInlineEntityList items={entityListItems} />
            ) : renderedToolOutput ? (
              <Box
                maxH={
                  renderConfig.constrainOutputHeight
                    ? TRACE_INLINE_SCROLL_MAX_HEIGHT
                    : undefined
                }
                overflow={
                  renderConfig.constrainOutputHeight ? 'auto' : undefined
                }
              >
                {renderedToolOutput}
              </Box>
            ) : !hasResult ? (
              <MetaDefault color="fg.subtle" fontStyle="italic">
                {t('noOutput')}
              </MetaDefault>
            ) : null}
          </VStack>
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
